// Accordion.ts
interface AccordionOptions {
  moduleSelector: string;
  itemSelector: string;
  titleSelector: string;
  contentSelector: string;
  buttonSelector: string;
  duration: number;
  easing: string;
}

export class AccordionManager {
  private modules: HTMLElement[] = [];
  private triggers: HTMLButtonElement[] = [];
  private options: AccordionOptions;

  constructor(options: Partial<AccordionOptions> = {}) {
    // Default options matching your HTML structure
    this.options = {
      moduleSelector: '.flexible-module__accordion',
      itemSelector: '.flexible-module__accordion-item',
      titleSelector: '.flexible-module__accordion-item-title',
      contentSelector: '.flexible-module__accordion-item-content',
      buttonSelector: '.flexible-module__accordion-item-title button',
      duration: 300,
      easing: 'ease-in-out',
      ...options
    };
  }

  public init(): void {
    // Find all accordion modules on the page
    this.modules = Array.from(
      document.querySelectorAll(this.options.moduleSelector)
    );

    if (this.modules.length === 0) {
      return;
    }

    // Process each accordion module
    this.modules.forEach(module => {
      this.initializeModule(module);
    });
  }

  private initializeModule(module: HTMLElement): void {
    // Get all triggers within this module
    const moduleTriggers = Array.from(
      module.querySelectorAll(this.options.buttonSelector)
    ) as HTMLButtonElement[];

    // Add these triggers to our collection
    this.triggers = [ ...this.triggers, ...moduleTriggers ];

    // Initialize each accordion item
    moduleTriggers.forEach(trigger => {
      this.setupAccordionItem(trigger);
    });

    console.log(`Initialized ${moduleTriggers.length} accordion items in module`);
  }

  private setupAccordionItem(trigger: HTMLButtonElement): void {
    // Get associated panel using aria-controls
    const panelId: string = trigger.getAttribute('aria-controls') || '';
    const panel = document.getElementById(panelId);

    if (!panel) {
      console.warn(`No panel found with ID: ${panelId}`);
      return;
    }

    // Set initial panel state
    panel.style.overflow = 'hidden';

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      panel.style.height = 'auto';
      panel.style.display = 'block';
      panel.setAttribute('aria-hidden', 'false');
    } else {
      panel.style.height = '0';
      panel.style.display = 'none';
      panel.setAttribute('aria-hidden', 'true');
    }

    // Add click event listener
    trigger.addEventListener('click', () => {
      this.toggleAccordion(trigger);
    });

    // Add keyboard navigation within the same module
    trigger.addEventListener('keydown', (event: KeyboardEvent) => {
      this.handleKeyDown(event, trigger);
    });
  }

  private handleKeyDown(event: KeyboardEvent, trigger: HTMLButtonElement): void {
    // Find the parent module to scope keyboard navigation
    const item = trigger.closest(this.options.itemSelector);
    const module = item?.closest(this.options.moduleSelector);

    if (!module) return;

    // Get all triggers within this specific module
    const moduleTriggers = Array.from(
      module.querySelectorAll(this.options.buttonSelector)
    ) as HTMLButtonElement[];

    const index = moduleTriggers.indexOf(trigger);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moduleTriggers[(index + 1) % moduleTriggers.length].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        moduleTriggers[(index - 1 + moduleTriggers.length) % moduleTriggers.length].focus();
        break;
      case 'Home':
        event.preventDefault();
        moduleTriggers[0].focus();
        break;
      case 'End':
        event.preventDefault();
        moduleTriggers[moduleTriggers.length - 1].focus();
        break;
    }
  }

  private toggleAccordion(trigger: HTMLButtonElement): void {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const panelId: string = trigger.getAttribute('aria-controls') || '';
    const panel = document.getElementById(panelId);

    if (!panel) return;

    // Update ARIA attributes
    trigger.setAttribute('aria-expanded', (!isExpanded).toString());
    panel.setAttribute('aria-hidden', isExpanded.toString());

    // update the icon inside the trigger
    const icon = trigger.querySelector('i');
    if (icon) {
      icon.classList.toggle('fa-minus', !isExpanded);
      icon.classList.toggle('fa-plus', isExpanded);
    }

    if (isExpanded) {
      this.animateClose(panel);
    } else {
      this.animateOpen(panel);
    }
  }

  private animateOpen(panel: HTMLElement): void {
    // Set up for animation
    panel.style.display = 'block';
    panel.style.height = '0';

    // Need to get the scrollHeight before starting animation
    const targetHeight = panel.scrollHeight;

    // Configure animation
    panel.style.transition = `height ${this.options.duration}ms ${this.options.easing}`;

    // Start animation
    requestAnimationFrame(() => {
      panel.style.height = `${targetHeight}px`;

      // Clean up after animation completes
      this.onAnimationComplete(panel, true);
    });
  }

  private animateClose(panel: HTMLElement): void {
    // Set current height explicitly before animating
    const startHeight = panel.scrollHeight;
    panel.style.height = `${startHeight}px`;

    // Force a reflow to ensure the explicit height takes effect
    void panel.offsetHeight; // Fixed TypeScript error with void operator

    // Configure animation
    panel.style.transition = `height ${this.options.duration}ms ${this.options.easing}`;

    // Start animation
    requestAnimationFrame(() => {
      panel.style.height = '0';

      // Clean up after animation completes
      this.onAnimationComplete(panel, false);
    });
  }

  private onAnimationComplete(panel: HTMLElement, isOpen: boolean): void {
    // Wait for the animation to complete
    setTimeout(() => {
      // Clean up transition
      panel.style.transition = '';

      if (isOpen) {
        // If open, set height to auto to allow for content changes
        panel.style.height = 'auto';
      } else {
        // If closed, hide completely
        panel.style.display = 'none';
      }
    }, this.options.duration);
  }
}
